import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CheckCheck,
  Trash2,
  Info,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Megaphone,
  UserPlus,
  BarChart3,
  Loader2
} from 'lucide-react';
import { notificationService, Notification } from '../../services/notificationService';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import toast from 'react-hot-toast';

type FilterStatus = 'all' | 'unread' | 'read';

const typeIcons: Record<string, React.ReactNode> = {
  info: <Info className="w-5 h-5 text-blue-500" />,
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
  error: <XCircle className="w-5 h-5 text-red-500" />,
  campaign: <Megaphone className="w-5 h-5 text-purple-500" />,
  lead: <UserPlus className="w-5 h-5 text-emerald-500" />,
  report: <BarChart3 className="w-5 h-5 text-orange-500" />,
};

const typeBgColors: Record<string, string> = {
  info: 'bg-blue-100 dark:bg-blue-900/30',
  success: 'bg-green-100 dark:bg-green-900/30',
  warning: 'bg-yellow-100 dark:bg-yellow-900/30',
  error: 'bg-red-100 dark:bg-red-900/30',
  campaign: 'bg-purple-100 dark:bg-purple-900/30',
  lead: 'bg-emerald-100 dark:bg-emerald-900/30',
  report: 'bg-orange-100 dark:bg-orange-900/30',
};

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (filter !== 'all') params.status = filter;

      const response = await notificationService.getNotifications(params);
      setNotifications(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch {
      toast.error(t('notifications.fetchError', 'Error loading notifications'));
    } finally {
      setIsLoading(false);
    }
  }, [page, filter, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success(t('notifications.allMarkedRead', 'All marked as read'));
    } catch {
      toast.error(t('common.error', 'Error'));
    }
  };

  const handleClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await notificationService.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
      } catch {
        // silently fail
      }
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await notificationService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success(t('notifications.deleted', 'Notification deleted'));
    } catch {
      toast.error(t('common.error', 'Error'));
    }
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return t('notifications.justNow', 'Just now');
    if (diffMin < 60) return t('notifications.minutesAgo', '{{count}} min ago', { count: diffMin });
    if (diffHrs < 24) return t('notifications.hoursAgo', '{{count}}h ago', { count: diffHrs });
    if (diffDays < 7) return t('notifications.daysAgo', '{{count}}d ago', { count: diffDays });
    return date.toLocaleDateString();
  };

  const filters: { key: FilterStatus; label: string }[] = [
    { key: 'all', label: t('notifications.filters.all', 'All') },
    { key: 'unread', label: t('notifications.filters.unread', 'Unread') },
    { key: 'read', label: t('notifications.filters.read', 'Read') },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="max-w-3xl mx-auto space-y-3 sm:space-y-6">
      {/* Header */}
      <PageHeader
        icon={Bell}
        title={t('notifications.title', 'Notificações')}
        description={unreadCount > 0
          ? t('notifications.unreadCount', '{{count}} não lida(s)', { count: unreadCount })
          : t('notifications.pageSubtitle', 'Avisos sobre seus posts, anúncios e contatos.')}
        actions={unreadCount > 0 ? (
          <Button variant="secondary" size="sm" onClick={handleMarkAllAsRead} className="gap-2 w-full sm:w-auto">
            <CheckCheck className="w-4 h-4" />
            {t('notifications.markAllRead', 'Marcar todas como lidas')}
          </Button>
        ) : undefined}
      />

      {/* Filters */}
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-full sm:w-fit">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setPage(1); }}
            className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-all ${
              filter === f.key
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              {t('notifications.noNotifications', 'No notifications')}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {t('notifications.noNotificationsDesc', 'Quando um post for publicado, um anúncio precisar de atenção ou chegar um contato novo, você fica sabendo aqui.')}
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleClick(notification)}
              className={`group flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all cursor-pointer ${
                notification.read
                  ? 'bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600'
                  : 'bg-primary-50/50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800/30 hover:border-primary-300 dark:hover:border-primary-700'
              }`}
            >
              <div className={`flex-shrink-0 p-2.5 rounded-xl ${typeBgColors[notification.type] || typeBgColors.info}`}>
                {typeIcons[notification.type] || typeIcons.info}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`text-sm font-semibold ${
                    notification.read
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {notification.title}
                    {!notification.read && (
                      <span className="ml-2 inline-block w-2 h-2 bg-primary-500 rounded-full" />
                    )}
                  </h3>
                  <span className="text-xs text-gray-400 dark:text-gray-500 sm:whitespace-nowrap flex-shrink-0">
                    {formatTime(notification.created_at)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                  {notification.message}
                </p>
              </div>

              <button
                onClick={(e) => handleDelete(e, notification.id)}
                className="flex-shrink-0 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
                title={t('notifications.delete', 'Delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t('common.previous', 'Previous')}
          </Button>
          <span className="text-sm text-gray-500 dark:text-gray-400 px-3">
            {page} / {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            {t('common.next', 'Next')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
